
import { useState, useEffect, useCallback } from "react";
import { Firestore, doc } from "firebase/firestore";
import { toast } from "sonner";
import { useFirestoreError } from "@/hooks/useFirestoreError";
import { createOffer, getOffersByLocationId, updateOffer, deleteOffer } from "@/services/offerService";
import { offerToSessionEvent, enhanceOffersWithOptions } from "@/services/offer/offerDisplayUtils";
import { useSessionDialog } from "./useSessionDialog";
import { CampLocation } from "@/types/campLocation";
import { Offer } from "@/types/offer";
import { SortableOption } from "@/types/fieldsOptions";
import { getAllSortableOptions } from "@/services/getSortableOptions";

export function useOfferManagement(
  locationId: string, 
  db: Firestore, 
  userId: string,
  location: CampLocation
) {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [sessionEvents, setSessionEvents] = useState<any[]>([]);
  const [lastOfferByVariation, setLastOfferByVariation] = useState<Record<string, Offer>>({});
  const [availabilityStatusOptions, setAvailabilityStatusOptions] = useState<SortableOption[]>([]);
  const [signupStatusOptions, setSignupStatusOptions] = useState<SortableOption[]>([]);
  const [allSortableOptionsMap, setAllSortableOptionsMap] = useState<Record<string, SortableOption>>({});
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  
  const { handleFirestoreError } = useFirestoreError();
  const dialogState = useSessionDialog();


  // Load sortable options when component mounts
  useEffect(() => {
    const loadOptions = async () => {
      try {
        // Get ALL sortable options as a map for efficient lookups
        const optionsMap = await getAllSortableOptions(db);
        setAllSortableOptionsMap(optionsMap);
        
        
        // Filter options by type
        const avOptions = Object.values(optionsMap)
          .filter(option => option.type === "availStatus")
          .sort((a, b) => a.sortOrder - b.sortOrder);
          
        const suOptions = Object.values(optionsMap)
          .filter(option => option.type === "signUpStatus")
          .sort((a, b) => a.sortOrder - b.sortOrder);
        
        
        setAvailabilityStatusOptions(avOptions);
        setSignupStatusOptions(suOptions);

        // If we already have offers, enhance them with the loaded options
        if (offers.length > 0) {
          const enhancedOffers = enhanceOffersWithOptions(offers, optionsMap);
          setOffers(enhancedOffers);
          updateSessionEvents(enhancedOffers);
        }
      } catch (error) {
        console.error("Error loading sortable options:", error);
      }
    };
    
    if (db) {
      loadOptions();
    }
  }, [db]);

  // Fetch offers when component mounts or locationId changes
  useEffect(() => {
    if (locationId && db) {
      loadOffers();
    }
  }, [locationId, db]);

  // Helper function to update session events with index-based colors
  const updateSessionEvents = (enhancedOffers: Offer[]) => {
    const events = enhancedOffers.map(offer => {
      // Find the program index in variations array for consistent color assignment
      const programIndex = location.variations?.findIndex(v => v.key === offer.variationKey) ?? -1;
      const event = offerToSessionEvent(offer, programIndex >= 0 ? programIndex : undefined);
      return event;
    });
    setSessionEvents(events);
  };

  // Load offers from Firestore
  const loadOffers = useCallback(async () => {
    setIsLoading(true);
    try {
      const fetchedOffers = await getOffersByLocationId(db, locationId);
      
      // Enhance offers with status option objects from our map
      const enhancedOffers = enhanceOffersWithOptions(
        fetchedOffers, 
        allSortableOptionsMap
      );
      
      setOffers(enhancedOffers);
      
      // Convert offers to session events for calendar with index-based colors
      updateSessionEvents(enhancedOffers);
      
      // Track most recent offer by variation
      const variationOfferMap: Record<string, Offer> = {};
      enhancedOffers.forEach(offer => {
        if (offer.variationKey) {
          const existingOffer = variationOfferMap[offer.variationKey];
          if (!existingOffer || 
              new Date(offer.startDate as any) > new Date(existingOffer.startDate as any)) {
            variationOfferMap[offer.variationKey] = offer;
          }
        }
      });
      
      setLastOfferByVariation(variationOfferMap);
      
    } catch (error) {
      console.error("Error loading offers:", error);
      handleFirestoreError(error);
    } finally {
      setIsLoading(false);
    }
  }, [db, locationId, handleFirestoreError, allSortableOptionsMap, location.variations]);

  // Add a new offer
  const addOffer = async (offerData: any) => {
    if (!locationId || !db || !userId) {
      toast.error("Missing required data to create offer");
      return null;
    }
    
    try {
      const newOffer = await createOffer(db, locationId, offerData, userId);
      
      if (newOffer) {
        
        // Find matching status options from our map
        const availabilityOption = allSortableOptionsMap[newOffer.availabilityStatus];
        const signupOption = allSortableOptionsMap[newOffer.signupStatus];
        
        
        // Add options to the new offer
        const enhancedOffer = {
          ...newOffer,
          availabilityStatusOption: availabilityOption,
          signupStatusOption: signupOption
        };
        
        // Update state with new offer
        setOffers(prev => [...prev, enhancedOffer]);
        
        // Update session events for calendar with index-based color
        const programIndex = location.variations?.findIndex(v => v.key === newOffer.variationKey) ?? -1;
        const newEvent = offerToSessionEvent(enhancedOffer, programIndex >= 0 ? programIndex : undefined);
        setSessionEvents(prev => [...prev, newEvent]);
        
        // Update last offer by variation
        if (newOffer.variationKey) {
          setLastOfferByVariation(prev => ({
            ...prev,
            [newOffer.variationKey]: enhancedOffer
          }));
        }
        
        return enhancedOffer;
      }
      
      return null;
    } catch (error) {
      console.error("Error creating offer:", error);
      handleFirestoreError(error);
      return null;
    }
  };

  // Update an existing offer. Resolves `true` when the write landed and `false`
  // when it did not (both failure paths toast it themselves unless
  // `suppressToasts`), so a caller never reports success over a failed write.
  const updateOfferItem = async (id: string, offerData: Partial<Offer>, suppressToasts: boolean = false): Promise<boolean> => {
    if (!db || !userId) {
      if (!suppressToasts) {
        toast.error("Missing required data to update offer");
      }
      return false;
    }
    
    try {
      const updatedOffer = await updateOffer(db, id, offerData, userId);
      
      if (updatedOffer) {
        
        // Find matching status options from our map
        const availabilityOption = allSortableOptionsMap[updatedOffer.availabilityStatus];
        const signupOption = allSortableOptionsMap[updatedOffer.signupStatus];
        
        
        // Add options to the updated offer
        const enhancedOffer = {
          ...updatedOffer,
          availabilityStatusOption: availabilityOption,
          signupStatusOption: signupOption
        };
        
        // Update offers state
        setOffers(prev => prev.map(o => o.id === id ? enhancedOffer : o));
        
        // Update session events for calendar with index-based color
        const programIndex = location.variations?.findIndex(v => v.key === updatedOffer.variationKey) ?? -1;
        const updatedEvent = offerToSessionEvent(enhancedOffer, programIndex >= 0 ? programIndex : undefined);
        setSessionEvents(prev => prev.map(e => e.id === id ? updatedEvent : e));
        
        // Update last offer by variation
        if (updatedOffer.variationKey) {
          setLastOfferByVariation(prev => ({
            ...prev,
            [updatedOffer.variationKey]: enhancedOffer
          }));
        }
      }
      return true;
    } catch (error) {
      console.error("Error updating offer:", error);
      if (!suppressToasts) {
        handleFirestoreError(error);
      }
      return false;
    }
  };

  // Delete an offer
  const deleteOfferItem = useCallback(async (id: string): Promise<void> => {
    if (!id) {
      console.error("DEBUG - useOfferManagement - No offer ID provided for deletion");
      return;
    }
    
    try {
      const success = await deleteOffer(db, id);
      
      if (success) {
        
        // Remove from state
        const offerToRemove = offers.find(o => o.id === id);
        setOffers(prev => prev.filter(o => o.id !== id));
        setSessionEvents(prev => prev.filter(e => e.id !== id));
        
        // Update last offer by variation if needed
        if (offerToRemove && offerToRemove.variationKey) {
          const variationKey = offerToRemove.variationKey;
          const remainingOffers = offers.filter(o => 
            o.id !== id && o.variationKey === variationKey
          );
          
          if (remainingOffers.length > 0) {
            // Find the most recent one
            const mostRecent = remainingOffers.reduce((latest, current) => {
              return new Date(current.startDate as any) > new Date(latest.startDate as any) 
                ? current : latest;
            }, remainingOffers[0]);
            
            setLastOfferByVariation(prev => ({
              ...prev,
              [variationKey]: mostRecent
            }));
          } else {
            // No more offers for this variation
            setLastOfferByVariation(prev => {
              const updated = { ...prev };
              delete updated[variationKey];
              return updated;
            });
          }
        }
        
        // Return true to indicate success
        return;
      }
      
      // If we reach here, deletion was not successful
      console.error("DEBUG - useOfferManagement - deleteOffer returned false for ID:", id);
      throw new Error("Delete operation failed");
      
    } catch (error) {
      console.error("DEBUG - useOfferManagement - Error deleting offer:", error);
      handleFirestoreError(error);
      throw error; // Rethrow to allow caller to handle
    }
  }, [db, offers, handleFirestoreError]);

  // After loading sortable options map, make sure to enhance existing offers
  useEffect(() => {
    if (Object.keys(allSortableOptionsMap).length > 0 && offers.length > 0) {
      const enhancedOffers = enhanceOffersWithOptions(offers, allSortableOptionsMap);
      setOffers(enhancedOffers);
      updateSessionEvents(enhancedOffers);
    }
  }, [allSortableOptionsMap]);

  return {
    offers,
    sessionEvents,
    isLoading,
    addOffer,
    updateOfferItem,
    deleteOfferItem,
    lastOfferByVariation,
    refreshOffers: loadOffers,
    availabilityStatusOptions,
    signupStatusOptions,
    selectedDate,
    setSelectedDate,
    ...dialogState
  };
}
